// ============================================================================
// SalaryMasterGrid (GC-B) — grille salariale MAITRE, lecture seule (CE/admin)
// ============================================================================
// Port FIDELE du PoC app/(dashboard)/grille-salariale/salary-master-grid.tsx
// (425 L @ SHA 7e40e4a). Par classe, UNE table transposee (echelons en colonnes) :
//  - 4 rangees de taux (taux horaire annee, salaire annuel, Δ echelon, Taux N-1) ;
//  - 1 rangee par employe, triee niveau 1 -> sans-niveau, bande brand en fond de
//    l'echelon 1 a l'echelon_max de SON niveau, marqueurs par periode (Actuel /
//    passe / scenario) a chaque echelon. Postes/criteres du niveau en info-bulle.
// Le toggle « Reduire la grille » masque les employes et montre une rangee-bande
// anonyme par niveau (postes en info-bulle). La grille interactive des directeurs
// reste EmployeeGrid (planification, L05) — celle-ci est CE, lecture seule.
//
// TRANSPOSITION (PLAN-ATTAQUE §c.6.4, calque EmployeeGrid SL-C) :
//  - Section shadcn -> carte GS_COMPONENTS.card + en-tete eyebrow/title/description ;
//  - Badge variant -> Badge (tone) de ../employee/ui (neutral/brand/warning/info) ;
//  - Button -> bouton natif GS_COMPONENTS.btnSecondary / iconBtn ;
//  - Tooltip riche -> attribut `title=` natif (motif EmployeeGrid) ;
//  - tokens shadcn (bg-muted/bg-card/text-muted-foreground/border-border/bg-primary)
//    -> slate + brand (bande = bg-brand-50 dark:bg-brand-950/40) ;
//  - HOURLY/PCT fr-CA figes -> formatHourly/formatPctPlain(locale) (P-CE18) ;
//  - formatCurrency -> formatCad(locale) ; libelles FR -> cles i18n FR+EN.

import { useMemo, useState } from 'react';
import { ChevronsDownUp, ChevronsUpDown, Minus, Plus } from 'lucide-react';
import { GS_COMPONENTS } from '@gs/ui';

import { useTranslation } from '../../i18n';
import { cn } from '../../lib/utils';
import { Badge } from '../employee/ui';
import { ClassInfoButton } from './ClassInfoButton';
import { MarkerBadge } from './EmployeeGrid';
import { formatCad, formatHourly, formatPctPlain } from './format';
import type { EmployeeGridLine } from '../../lib/salary/positions';
import { annualFromHourly, round2 } from '../../lib/salary/projection';
import type { SalaryClass, SalaryClassNiveau, SalaryClassYear } from '../../lib/types/firestore';

// Bouton icone compact (motif partage EmployeeGrid — remplace Button ghost size-5).
const iconBtn =
  'inline-flex size-5 items-center justify-center rounded-md text-slate-400 transition-colors ' +
  'hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300 touch-manipulation';

export interface SalaryMasterGridProps {
  /** Grille de l'annee selectionnee (master ou snapshot annuel). */
  classes: SalaryClass[];
  /** Lignes employes keyees par id de classe MASTER (voir masterClassId). */
  linesByClass: Record<string, EmployeeGridLine[]>;
  year: number;
  /** Ombrer les employes inactifs (vue d'une annee passee). */
  shadeInactive?: boolean;
  /** Actions a droite de l'en-tete (ex. YearPicker + boutons export). */
  headerActions?: React.ReactNode;
}

export function SalaryMasterGrid({
  classes,
  linesByClass,
  year,
  shadeInactive = false,
  headerActions,
}: SalaryMasterGridProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Membres ayant au moins une ligne hors de leur classe courante -> « + ».
  const idsWithOtherLines = useMemo(() => {
    const ids = new Set<string>();
    for (const lines of Object.values(linesByClass)) {
      for (const l of lines) {
        if (!l.isCurrentClass && !l.virtual) ids.add(l.id);
      }
    }
    return ids;
  }, [linesByClass]);

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <section className={`${GS_COMPONENTS.card} p-5`}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-chip font-medium uppercase tracking-wide text-slate-400">
            {t('gest:grilleCe.master.eyebrow')}
          </span>
          <h2 className="text-body font-semibold text-slate-800 dark:text-slate-100">
            {t('gest:grilleCe.master.title')}
          </h2>
          <p className="max-w-3xl text-field text-slate-500 dark:text-slate-400">
            {t('gest:grilleCe.master.description')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className={`${GS_COMPONENTS.btnSecondary} inline-flex touch-manipulation items-center gap-1.5`}
          >
            {collapsed ? (
              <>
                <ChevronsUpDown size={16} aria-hidden="true" />
                {t('gest:grilleCe.master.showEmployees')}
              </>
            ) : (
              <>
                <ChevronsDownUp size={16} aria-hidden="true" />
                {t('gest:grilleCe.master.collapse')}
              </>
            )}
          </button>
          {headerActions}
        </div>
      </div>

      <div className="flex flex-col gap-8">
        {classes.map((cls) => (
          <MasterClassBlock
            key={cls.id}
            cls={cls}
            lines={linesByClass[masterClassId(cls)] ?? []}
            year={year}
            collapsed={collapsed}
            expanded={expanded}
            idsWithOtherLines={idsWithOtherLines}
            onToggleExpanded={toggleExpanded}
            shadeInactive={shadeInactive}
          />
        ))}

        {/* Legende des periodes (sans objet en mode reduit). */}
        {!collapsed ? (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-meta text-slate-500 dark:text-slate-400">
            <span className="inline-flex items-center gap-1.5">
              <Badge tone="neutral">’25</Badge> {t('gest:grilleCe.master.legendPast')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Badge tone="brand">{t('gest:grilleCe.master.legendCurrentBadge')}</Badge>{' '}
              {t('gest:grilleCe.master.legendCurrent')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Badge tone="warning">{t('gest:grilleCe.master.legendScenarioBadge')}</Badge>{' '}
              {t('gest:grilleCe.master.legendScenario')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Badge tone="info">{t('gest:grilleCe.master.legendFictifBadge')}</Badge>{' '}
              {t('gest:grilleCe.master.legendFictif')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-3.5 w-6 rounded bg-brand-50 dark:bg-brand-950/40" />{' '}
              {t('gest:grilleCe.master.legendBand')}
            </span>
          </div>
        ) : null}
      </div>
    </section>
  );
}

// Les snapshots annuels ont un id composite `{year}_{classId}` mais portent
// class_id ; les lignes employes sont keyees par l'id de classe MASTER.
function masterClassId(cls: SalaryClass): string {
  return (cls as Partial<SalaryClassYear>).class_id ?? cls.id;
}

// Borne droite (en echelons) de la bande d'un niveau.
function bandMax(niveau: SalaryClassNiveau, total: number): number {
  return Math.max(1, Math.min(niveau.echelon_max ?? total, total));
}

// Texte de l'info-bulle d'un niveau (« Niveau N — label » + description multiligne).
function niveauTooltipText(niveau: SalaryClassNiveau): string {
  const head = niveau.label ? `Niveau ${niveau.level} — ${niveau.label}` : `Niveau ${niveau.level}`;
  const desc = (niveau.description ?? '').trim();
  return desc ? `${head}\n${desc}` : head;
}

function MasterClassBlock({
  cls,
  lines,
  year,
  collapsed,
  expanded,
  idsWithOtherLines,
  onToggleExpanded,
  shadeInactive,
}: {
  cls: SalaryClass;
  lines: EmployeeGridLine[];
  year: number;
  collapsed: boolean;
  expanded: Set<string>;
  idsWithOtherLines: Set<string>;
  onToggleExpanded: (id: string) => void;
  shadeInactive: boolean;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const hours = {
    base_hours_per_week: cls.base_hours_per_week,
    weeks_per_year: cls.weeks_per_year,
  };
  const total = cls.echelons.length;

  // Lignes visibles, triees niveau 1 -> sans-niveau (lignes courantes d'abord,
  // puis passe inter-classe deplie, puis fictifs — ordre alpha a egalite).
  const visibleLines = lines
    .filter((l) => l.isCurrentClass || l.hasScenario || expanded.has(l.id))
    .sort((a, b) => {
      const rank = (l: EmployeeGridLine) => (l.virtual ? 2 : l.isCurrentClass ? 0 : 1);
      if (rank(a) !== rank(b)) return rank(a) - rank(b);
      const niv = (l: EmployeeGridLine) => l.niveau ?? Number.MAX_SAFE_INTEGER;
      if (niv(a) !== niv(b)) return niv(a) - niv(b);
      return a.name.localeCompare(b.name);
    });

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-1">
          <h3 className="text-field font-semibold tracking-tight text-slate-800 dark:text-slate-100">
            {cls.label}
          </h3>
          <ClassInfoButton description={cls.description} niveaux={cls.niveaux} />
        </div>
        {cls.aug_grille_pct !== null ? (
          <span className="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-1 text-chip font-medium text-brand-700 dark:bg-brand-950/50 dark:text-brand-300">
            {t('gest:grilleCe.master.augLabel', { pct: formatPctPlain(cls.aug_grille_pct, locale) })}
          </span>
        ) : null}
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full border-collapse whitespace-nowrap text-field">
          <thead>
            <tr className="bg-slate-100 uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-400">
              <th className="sticky left-0 bg-slate-100 px-3 py-2 text-left text-chip font-medium dark:bg-slate-800">
                {t('gest:grilleCe.master.echelonHeader')}
              </th>
              {cls.echelons.map((e) => (
                <th key={e.echelon} className="px-3 py-2 text-right text-chip font-medium">
                  {e.echelon}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Taux horaire {year}. */}
            <tr className="border-t border-slate-200 dark:border-slate-700">
              <th className="sticky left-0 bg-white px-3 py-2 text-left font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {t('gest:grilleCe.master.hourlyRow', { year })}
              </th>
              {cls.echelons.map((e) => (
                <td key={e.echelon} className="px-3 py-2 text-right font-semibold tabular-nums text-slate-800 dark:text-slate-100">
                  {formatHourly(round2(e.rate), locale)}
                </td>
              ))}
            </tr>
            {/* Salaire annuel (base_hours h × weeks). */}
            <tr className="border-t border-slate-200 dark:border-slate-700">
              <th className="sticky left-0 bg-white px-3 py-2 text-left font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                {t('gest:grilleCe.master.annualRow', { hours: cls.base_hours_per_week, weeks: cls.weeks_per_year })}
              </th>
              {cls.echelons.map((e) => (
                <td key={e.echelon} className="px-3 py-2 text-right tabular-nums text-slate-500 dark:text-slate-400">
                  {formatCad(annualFromHourly(e.rate, hours), locale)}
                </td>
              ))}
            </tr>
            {/* Δ echelon (pct_from_prev). */}
            <tr className="border-t border-slate-200 dark:border-slate-700">
              <th className="sticky left-0 bg-white px-3 py-2 text-left font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                {t('gest:grilleCe.master.deltaRow')}
              </th>
              {cls.echelons.map((e) => (
                <td key={e.echelon} className="px-3 py-2 text-right tabular-nums text-slate-500 dark:text-slate-400">
                  {e.pct_from_prev !== null ? formatPctPlain(e.pct_from_prev, locale) : '—'}
                </td>
              ))}
            </tr>
            {/* Taux {year - 1} (prev_year_rate). */}
            <tr className="border-t border-slate-200 dark:border-slate-700">
              <th className="sticky left-0 bg-white px-3 py-2 text-left font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                {t('gest:grilleCe.master.prevYearRow', { year: year - 1 })}
              </th>
              {cls.echelons.map((e) => (
                <td key={e.echelon} className="px-3 py-2 text-right tabular-nums text-slate-500 dark:text-slate-400">
                  {e.rate_2025 !== null ? formatHourly(e.rate_2025, locale) : '—'}
                </td>
              ))}
            </tr>

            {collapsed
              ? // Mode reduit : une rangee-bande anonyme par niveau, postes en info-bulle.
                cls.niveaux.map((n) => {
                  const max = bandMax(n, total);
                  const tip = niveauTooltipText(n);
                  return (
                    <tr key={n.level} className="border-t border-slate-200 dark:border-slate-700">
                      <th
                        title={tip}
                        className="sticky left-0 bg-white px-3 py-2 text-left font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                      >
                        <span className="cursor-help underline decoration-dotted underline-offset-2">
                          {t('gest:grilleCe.master.niveauShort', { level: n.level })}
                        </span>
                      </th>
                      <td colSpan={max} className="px-3 py-2" title={tip}>
                        <span className="block h-5 w-full rounded bg-brand-50 dark:bg-brand-950/40" />
                      </td>
                      {max < total ? <td colSpan={total - max} /> : null}
                    </tr>
                  );
                })
              : // Mode deplie : une rangee par employe, bande du niveau en fond.
                visibleLines.map((line) => {
                  const niveauDef =
                    line.niveau != null
                      ? (cls.niveaux.find((n) => n.level === line.niveau) ?? null)
                      : null;
                  const max = niveauDef ? bandMax(niveauDef, total) : 0;
                  return (
                    <tr
                      key={line.id}
                      className={cn(
                        'border-t border-slate-200 dark:border-slate-700',
                        shadeInactive && !line.active && 'opacity-60',
                      )}
                    >
                      <th
                        scope="row"
                        className={cn(
                          'sticky left-0 bg-white px-3 py-1.5 text-left text-chip font-medium dark:bg-slate-800',
                          line.isCurrentClass && !line.virtual
                            ? 'text-slate-800 dark:text-slate-100'
                            : 'text-slate-500 dark:text-slate-400',
                        )}
                      >
                        <span className="inline-flex items-center gap-1">
                          <span
                            className={cn(
                              'max-w-40 truncate',
                              // Barre = ligne de PASSE dans une autre classe.
                              !line.isCurrentClass &&
                                !line.hasScenario &&
                                'text-slate-400 line-through dark:text-slate-500',
                            )}
                            title={line.name}
                          >
                            {line.name}
                          </span>
                          {niveauDef ? (
                            <Badge tone="neutral" title={niveauTooltipText(niveauDef)}>
                              <span className="cursor-help">
                                {t('gest:grilleCe.master.niveauBadge', { level: niveauDef.level })}
                              </span>
                            </Badge>
                          ) : null}
                          {line.virtual ? (
                            <Badge tone="info">{t('gest:grilleCe.master.fictifBadge')}</Badge>
                          ) : null}
                          {line.isCurrentClass && idsWithOtherLines.has(line.id) ? (
                            <button
                              type="button"
                              className={iconBtn}
                              aria-expanded={expanded.has(line.id)}
                              aria-label={
                                expanded.has(line.id)
                                  ? t('gest:grilleCe.master.collapseAria', { name: line.name })
                                  : t('gest:grilleCe.master.expandAria', { name: line.name })
                              }
                              onClick={() => onToggleExpanded(line.id)}
                            >
                              {expanded.has(line.id) ? (
                                <Minus size={14} aria-hidden="true" />
                              ) : (
                                <Plus size={14} aria-hidden="true" />
                              )}
                            </button>
                          ) : null}
                        </span>
                      </th>
                      {cls.echelons.map((e) => {
                        const markers = line.markers[e.echelon] ?? [];
                        return (
                          <td
                            key={e.echelon}
                            className={cn(
                              'px-1 py-1 text-center align-middle',
                              e.echelon <= max && 'bg-brand-50 dark:bg-brand-950/40',
                            )}
                          >
                            {markers.length ? (
                              <span className="inline-flex flex-wrap items-center justify-center gap-0.5 text-chip">
                                {markers.map((m, i) => (
                                  <MarkerBadge key={i} marker={m} virtual={line.virtual} />
                                ))}
                              </span>
                            ) : null}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
